import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Send,
  ArrowLeft,
  User,
  CheckCheck,
  Circle,
} from "lucide-react";

const AdminChat = () => {
  const { clientUserId } = useParams();
  const { user, token, API, isSuperAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (clientUserId) {
      loadChat(clientUserId);
    }
  }, [clientUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchData = async () => {
    try {
      // Get conversations and assigned clients
      const [convRes, clientsRes] = await Promise.all([
        fetch(`${API}/chat/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (convRes.ok) {
        const data = await convRes.json();
        setConversations(data);
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setAssignedClients(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = API.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/chat/ws/${token}`);

    ws.onopen = () => {
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message" || data.type === "message_sent") {
        const msg = data.message;
        if (selectedClient && 
            (msg.sender_id === selectedClient.user_id || msg.receiver_id === selectedClient.user_id)) {
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === msg.message_id)) return prev;
            return [...prev, msg];
          });
        }
        fetchData(); // Refresh conversations
      }
      
      if (data.type === "messages_read" && selectedClient) {
        setMessages((prev) =>
          prev.map((m) =>
            m.sender_id === user.user_id && m.receiver_id === data.reader_id
              ? { ...m, read: true }
              : m
          )
        );
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  };

  const loadChat = async (userId) => {
    try {
      // Find client info
      const client = assignedClients.find((c) => c.user_id === userId);
      if (client) {
        setSelectedClient({ ...client, user_id: userId });
      } else {
        // Try to get from conversations
        const conv = conversations.find((c) => c.partner?.user_id === userId);
        if (conv) {
          setSelectedClient(conv.partner);
        }
      }

      const response = await fetch(`${API}/chat/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedClient || sending) return;

    setSending(true);
    try {
      const params = new URLSearchParams({
        receiver_id: selectedClient.user_id,
        content: newMessage.trim(),
      });

      const response = await fetch(`${API}/chat/messages?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setNewMessage("");
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Combine conversations and assigned clients for the list
  const getChatList = () => {
    const chatMap = new Map();

    // Add conversations
    conversations.forEach((conv) => {
      if (conv.partner) {
        chatMap.set(conv.partner.user_id, {
          ...conv.partner,
          lastMessage: conv.last_message,
          unreadCount: conv.unread_count,
        });
      }
    });

    // Add assigned clients that aren't in conversations yet
    assignedClients.forEach((client) => {
      if (!chatMap.has(client.user_id)) {
        chatMap.set(client.user_id, {
          user_id: client.user_id,
          name: client.name || client.user?.name,
          email: client.email || client.user?.email,
          role: "client",
          lastMessage: null,
          unreadCount: 0,
        });
      }
    });

    return Array.from(chatMap.values());
  };

  const chatList = getChatList();

  return (
    <AdminLayout activeNav="chat">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-2">
            Chat
          </h1>
          <p className="text-rnb-gray">
            Message your {isSuperAdmin ? "" : "assigned "}clients
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-250px)]">
            {/* Chat List */}
            <Card className="lg:col-span-1 overflow-hidden">
              <CardHeader className="border-b border-slate-200 py-4">
                <CardTitle className="text-lg">Conversations</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto max-h-[calc(100vh-350px)]">
                {chatList.length > 0 ? (
                  chatList.map((client) => (
                    <button
                      key={client.user_id}
                      onClick={() => loadChat(client.user_id)}
                      className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                        selectedClient?.user_id === client.user_id ? "bg-rnb-light" : ""
                      }`}
                      data-testid={`chat-client-${client.user_id}`}
                    >
                      <div className="w-10 h-10 bg-rnb-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-rnb-blue" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-rnb-navy truncate">
                            {client.name}
                          </p>
                          {client.unreadCount > 0 && (
                            <Badge variant="default" className="ml-2">
                              {client.unreadCount}
                            </Badge>
                          )}
                        </div>
                        {client.lastMessage && (
                          <p className="text-sm text-rnb-gray truncate">
                            {client.lastMessage.content}
                          </p>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center text-rnb-gray">
                    <User className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No clients {isSuperAdmin ? "available" : "assigned"}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat Window */}
            <Card className="lg:col-span-2 flex flex-col overflow-hidden">
              {selectedClient ? (
                <>
                  {/* Chat Header */}
                  <CardHeader className="border-b border-slate-200 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-rnb-blue/10 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-rnb-blue" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{selectedClient.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          <Circle className={`h-2 w-2 ${wsConnected ? "fill-green-500 text-green-500" : "fill-gray-400 text-gray-400"}`} />
                          <span className="text-xs text-rnb-gray">
                            {wsConnected ? "Connected" : "Reconnecting..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(100vh-450px)]">
                    {messages.length > 0 ? (
                      messages.map((msg, index) => {
                        const isOwn = msg.sender_id === user.user_id;
                        const showDate = index === 0 || 
                          formatDate(msg.created_at) !== formatDate(messages[index - 1].created_at);

                        return (
                          <div key={msg.message_id}>
                            {showDate && (
                              <div className="text-center text-xs text-rnb-gray my-4">
                                {formatDate(msg.created_at)}
                              </div>
                            )}
                            <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                              <div
                                className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                  isOwn
                                    ? "bg-rnb-blue text-white rounded-br-md"
                                    : "bg-slate-100 text-rnb-navy rounded-bl-md"
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                <div className={`flex items-center justify-end gap-1 mt-1 ${
                                  isOwn ? "text-white/70" : "text-rnb-gray"
                                }`}>
                                  <span className="text-xs">{formatTime(msg.created_at)}</span>
                                  {isOwn && (
                                    <CheckCheck className={`h-3 w-3 ${msg.read ? "text-white" : "text-white/50"}`} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-rnb-gray py-12">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </CardContent>

                  {/* Message Input */}
                  <div className="border-t border-slate-200 p-4">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        data-testid="admin-chat-input"
                      />
                      <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="admin-chat-send">
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-rnb-gray">
                  <div className="text-center">
                    <User className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>Select a client to start chatting</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminChat;
